/**
 * Owner: Masa Bayu
 * File: font-resolver.ts
 * Date:Time: 2026-10-05 11:52
 * Description: Fasa 12 · Universal Font Name & Weight Resolver · BaseFont → CSS.
 */

/** PDF Font Descriptor ForceBold — Fasa 12 Test 12.4. */
export const PDF_FORCE_BOLD_FLAG = 0x0040_0000;

export type ResolvedFont = {
  readonly fontFamily: string;
  readonly fontWeight: number;
  readonly fontStyle: 'normal' | 'italic';
};

function decodePdfName(raw: string): string {
  return raw.replace(/#([0-9A-Fa-f]{2})/g, (_, hex: string) =>
    String.fromCharCode(Number.parseInt(hex, 16)),
  );
}

/** Token berat/gaya berdelimitter · elak rosakkan keluarga seperti BlackChancery. */
function hasStyleToken(lowerName: string, token: string): boolean {
  return new RegExp(`(?:^|[-_,\\s])${token}(?:$|[-_,\\s]|mt)`, 'i').test(lowerName);
}

function stripStyleTokens(name: string): string {
  // Delimiter wajib (- _ ,) · elak makan huruf keluarga (Cinzel… / BlackChancery).
  return name
    .replace(
      /[-_](?:extra|ultra)?(?:bold|black|heavy|light|medium|thin|hairline|regular|normal|book|roman|italic|oblique|demibold|semibold)+(?:mt)?/gi,
      '',
    )
    .replace(
      /,\s*(?:extra|ultra)?(?:Bold|Black|Heavy|Light|Medium|Thin|Hairline|Regular|Normal|Book|Roman|Italic|Oblique|DemiBold|SemiBold)+/gi,
      '',
    )
    // Hyphen mesti di hujung kelas · elak julat ASCII [,-_] makan huruf (C…).
    .replace(/^[-_,\s]+|[-_,\s]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Universal Font Resolver
 * Membersihkan nama fon PDF dan mengekstrak weight/style secara matematik.
 */
export function resolveFont(rawFontName: string): ResolvedFont {
  if (!rawFontName || rawFontName.trim().length === 0) {
    return { fontFamily: 'sans-serif', fontWeight: 400, fontStyle: 'normal' };
  }

  let cleanName = decodePdfName(rawFontName.trim());
  let weight = 400;
  let style: 'normal' | 'italic' = 'normal';

  // 1. Buang prefix subset PDF (contoh: "BCDMEE+CinzelDecorative-Black")
  const plusIndex = cleanName.indexOf('+');
  if (plusIndex !== -1) {
    cleanName = cleanName.substring(plusIndex + 1);
  }

  const lowerName = cleanName.toLowerCase();

  // 2. Ekstrak Style (Italic/Oblique)
  if (hasStyleToken(lowerName, 'italic') || hasStyleToken(lowerName, 'oblique')) {
    style = 'italic';
  }

  // 3. Ekstrak Weight (Mapping Typography Standard) · yang paling spesifik dahulu
  if (hasStyleToken(lowerName, 'thin') || hasStyleToken(lowerName, 'hairline')) {
    weight = 100;
  } else if (
    hasStyleToken(lowerName, 'extralight') ||
    hasStyleToken(lowerName, 'ultralight')
  ) {
    weight = 200;
  } else if (hasStyleToken(lowerName, 'light')) {
    weight = 300;
  } else if (hasStyleToken(lowerName, 'medium')) {
    weight = 500;
  } else if (
    hasStyleToken(lowerName, 'semibold') ||
    hasStyleToken(lowerName, 'demibold')
  ) {
    weight = 600;
  } else if (
    hasStyleToken(lowerName, 'extrabold') ||
    hasStyleToken(lowerName, 'ultrabold')
  ) {
    weight = 800;
  } else if (
    hasStyleToken(lowerName, 'black') ||
    hasStyleToken(lowerName, 'heavy')
  ) {
    weight = 900;
  } else if (hasStyleToken(lowerName, 'bold') || /bold/i.test(cleanName)) {
    // BoldMT / BoldItalic · /bold/i cover TimesNewRomanPS-BoldMT
    weight = 700;
  } else if (
    hasStyleToken(lowerName, 'regular') ||
    hasStyleToken(lowerName, 'normal') ||
    hasStyleToken(lowerName, 'book') ||
    hasStyleToken(lowerName, 'roman')
  ) {
    weight = 400;
  }

  cleanName = stripStyleTokens(cleanName);

  if (!cleanName) {
    cleanName = 'sans-serif';
  }

  return {
    fontFamily: cleanName,
    fontWeight: weight,
    fontStyle: style,
  };
}

/** Wrapper TDD Fasa 12 · nama + PDF ForceBold flag. */
export function resolveFontWeight(fontName: string, flags: number = 0): number {
  if ((flags & PDF_FORCE_BOLD_FLAG) !== 0) return 700;
  return resolveFont(fontName).fontWeight;
}
